/**
 * Registry of the bite-test UIs. There are three apps, each deployed on its own
 * subdomain:
 *
 *   tools  - Private Label (tools.bite-test.io)
 *   api    - the API screens (api.bite-test.io), which share the host with the
 *            bite-test-server API; the load balancer sends /api, /uploads and
 *            /webhooks to the server and everything else here
 *   admin  - user, permission and reference-data management (admin.bite-test.io)
 *
 * Access is still granted per screen (see SCREENS), so the roles an operator
 * hands out in the admin portal are finer-grained than the apps.
 */
export type AppId = "tools" | "api" | "admin";

export type ScreenId =
  | "home"
  | "tools"
  | "xml-screening"
  | "public-onboarding"
  | "public-screening"
  | "risk-screening"
  | "teams"
  | "super-admin"
  | "api-documentation"
  | "admin-users"
  | "client-access"
  | "admin-databases"
  | "admin-wikis";

/** Grants every screen. Screen roles are named bite-test-<screen>. */
export const ADMIN_ROLE = "bite-test-admin";

/** Grants every screen in the API app, and nothing in Private Label or Admin. */
export const API_DEMO_ROLE = "bite-test-api-demo";

export interface ScreenDefinition {
  id: ScreenId;
  app: AppId;
  label: string;
  /** Route inside the app. */
  path: string;
  /** Keycloak role that grants this screen. */
  role: string;
}

export const SCREENS: readonly ScreenDefinition[] = [
  { id: "tools", app: "tools", label: "Private Label", path: "/", role: "bite-test-tools" },
  {
    id: "xml-screening",
    app: "api",
    label: "XML Screening",
    path: "/xml-screening",
    role: "bite-test-xml-screening",
  },
  {
    id: "public-onboarding",
    app: "api",
    label: "Public API Onboarding",
    path: "/public-onboarding",
    role: ADMIN_ROLE,
  },
  {
    id: "public-screening",
    app: "api",
    label: "Public API Screening",
    path: "/public-screening",
    role: "bite-test-public-screening",
  },
  {
    id: "risk-screening",
    app: "api",
    label: "Risk Screening",
    path: "/risk-screening",
    role: "bite-test-risk-screening",
  },
  {
    id: "teams",
    app: "api",
    label: "Teams",
    path: "/teams",
    role: "bite-test-teams",
  },
  {
    id: "super-admin",
    app: "api",
    label: "Bite Admin Team Setup",
    path: "/super-admin",
    role: ADMIN_ROLE,
  },
  {
    id: "client-access",
    app: "api",
    label: "Manage Client Access",
    path: "/client-access",
    // The onboarding proxy behind this page is admin-only server-side.
    role: ADMIN_ROLE,
  },
  {
    id: "api-documentation",
    app: "api",
    label: "API Documentation",
    // Links out to the BITE environment's own reference and console, so this
    // screen reads the contract rather than calling the API.
    path: "/api-documentation",
    role: "bite-test-api-documentation",
  },
  // Administrators' landing page: an overview of every app and screen.
  { id: "home", app: "admin", label: "Home", path: "/", role: ADMIN_ROLE },
  { id: "admin-users", app: "admin", label: "Users & Permissions", path: "/users", role: ADMIN_ROLE },
    { id: "admin-databases", app: "admin", label: "Databases", path: "/databases", role: ADMIN_ROLE },
  { id: "admin-wikis", app: "admin", label: "Wikis", path: "/wikis", role: ADMIN_ROLE },
];

export interface NavItem {
  screen: ScreenId;
  /** Overrides the screen's own label in the side nav. */
  label?: string;
}

export interface NavSection {
  /** A section with a single item and no title renders as a plain link. */
  title?: string;
  items: readonly NavItem[];
}

/**
 * Side nav structure, shared by every app. Screens keep their own URLs; the nav
 * only groups them, and a section disappears when the user can open none of it.
 */
export const NAV_SECTIONS: readonly NavSection[] = [
  { items: [{ screen: "tools", label: "Private Label" }] },
  {
    title: "API Test",
    items: [
      { screen: "xml-screening", label: "CargoWise Screening" },
      { screen: "public-screening" },
      { screen: "risk-screening" },
    ],
  },
  { title: "Docs", items: [{ screen: "api-documentation" }] },
  { title: "Teams", items: [{ screen: "teams", label: "Team access" }, { screen: "super-admin" }] },
  {
    title: "Admin",
    items: [
      { screen: "public-onboarding" },
      { screen: "client-access" },
      { screen: "admin-users" },
      { screen: "admin-databases" },
      { screen: "admin-wikis" },
    ],
  },
];

export interface AppDefinition {
  id: AppId;
  label: string;
  /** Host label under the root domain. */
  subdomain: string;
  /** Port used by `npm start` in that app's directory. */
  devPort: number;
}

export const APPS: readonly AppDefinition[] = [
  { id: "tools", label: "Private Label", subdomain: "tools", devPort: 3301 },
  { id: "api", label: "API", subdomain: "api", devPort: 3302 },
  { id: "admin", label: "Admin", subdomain: "admin", devPort: 3303 },
];

export function getApp(id: AppId): AppDefinition {
  const app = APPS.find((entry) => entry.id === id);
  if (!app) throw new Error(`Unknown app: ${id}`);
  return app;
}

export function getScreen(id: ScreenId): ScreenDefinition {
  const screen = SCREENS.find((entry) => entry.id === id);
  if (!screen) throw new Error(`Unknown screen: ${id}`);
  return screen;
}

export function screensOf(app: AppId): ScreenDefinition[] {
  return SCREENS.filter((screen) => screen.app === app);
}

/** Mirrors the server's check (bite-test-server/src/auth/roles.ts). */
export function canAccessScreen(
  roles: readonly string[],
  screen: ScreenDefinition,
): boolean {
  return (
    roles.includes(ADMIN_ROLE) ||
    (screen.app === "api" &&
      screen.role !== ADMIN_ROLE &&
      roles.includes(API_DEMO_ROLE)) ||
    roles.includes(screen.role)
  );
}

/** An app is reachable when the user may open at least one of its screens. */
export function canAccessApp(roles: readonly string[], app: AppDefinition): boolean {
  return screensOf(app.id).some((screen) => canAccessScreen(roles, screen));
}

/**
 * Absolute URL of another app. `REACT_APP_ROOT_URL` is the root domain's
 * origin: on localhost the apps are addressed by port, anywhere else by
 * `<subdomain>.<root host>`.
 */
export function appUrl(id: AppId, path = ""): string {
  const app = getApp(id);
  const root = new URL(process.env.REACT_APP_ROOT_URL ?? window.location.origin);
  const isLocal =
    root.hostname === "localhost" || /^[\d.]+$/.test(root.hostname);
  const origin = isLocal
    ? `${root.protocol}//${root.hostname}:${app.devPort}`
    : `${root.protocol}//${app.subdomain}.${root.host}`;
  return `${origin}${path}`;
}

/** Absolute URL of a screen, wherever its app is hosted. */
export function screenUrl(id: ScreenId, search = ""): string {
  const screen = getScreen(id);
  return appUrl(screen.app, `${screen.path}${search}`);
}

/** Bite logo, bundled with the app so it does not depend on the API origin. */
export function logoUrl(): string {
  return `${process.env.PUBLIC_URL ?? ""}/bite-logo.svg`;
}
